import moment from 'moment';
import 'moment-timezone';
import { logger } from '../../pbir/utils/logger';

const LOGGER = logger('TimeZone');

export class TimeZone {
  static get local() { return TimeZone.from(getMachineLocalTimeZoneId()); }

  readonly id: string;
  readonly name: string;
  readonly utcOffset?: number;
  readonly offsetString?: string;

  constructor(properties: { id: string; name: string; }) {
    this.id = properties.id;
    this.name = properties.name;
    this.offsetString = moment.tz(properties.id).format('Z');
    this.utcOffset = parseUtcOffset(this.offsetString);
  }

  fromUtc(timestamp: Date | moment.Moment | string) {
    return convertTimeToTimeZone(timestamp, this);
  }

  toUtc(timestamp: Date | moment.Moment | string) {
    return convertTimeToUtc(timestamp, this);
  }

  static from(id: string) {
    return getTimeZoneById(id);
  }
}

export function getMachineLocalTimeZoneId() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  catch (err) {
    LOGGER.error(`Failed to guess user's own time zone`, err);
    return null;
  }
}

// Windows tzutil output with IANA ids
export const timeZones: TimeZone[] = [
  new TimeZone({ name: "International Date Line West", id: "Etc/GMT+12" }),
  new TimeZone({ name: "Coordinated Universal Time-11", id: "Etc/GMT+11" }),
  new TimeZone({ name: "Hawaii", id: "Pacific/Honolulu" }),
  new TimeZone({ name: "Marquesas Islands", id: "Pacific/Marquesas" }),
  new TimeZone({ name: "Alaska", id: "America/Anchorage" }),
  new TimeZone({ name: "Co-ordinated Universal Time-09", id: "Etc/GMT+9" }),
  new TimeZone({ name: "Baja California", id: "America/Tijuana" }),
  new TimeZone({ name: "Co-ordinated Universal Time-08", id: "Etc/GMT+8" }),
  new TimeZone({ name: "Pacific Time (US & Canada)", id: "America/Los_Angeles" }),
  new TimeZone({ name: "Arizona", id: "America/Phoenix" }),
  new TimeZone({ name: "Chihuahua, La Paz, Mazatlan", id: "America/Chihuahua" }),
  new TimeZone({ name: "Mountain Time (US & Canada)", id: "America/Denver" }),
  new TimeZone({ name: "Central America", id: "America/Guatemala" }),
  new TimeZone({ name: "Central Time (US & Canada)", id: "America/Chicago" }),
  new TimeZone({ name: "Easter Island", id: "Pacific/Easter" }),
  new TimeZone({ name: "Guadalajara, Mexico City, Monterrey", id: "America/Mexico_City" }),
  new TimeZone({ name: "Saskatchewan", id: "America/Regina" }),
  new TimeZone({ name: "Bogota, Lima, Quito, Rio Branco", id: "America/Bogota" }),
  new TimeZone({ name: "Chetumal", id: "America/Cancun" }),
  new TimeZone({ name: "Eastern Time (US & Canada)", id: "America/New_York" }),
  new TimeZone({ name: "Haiti", id: "America/Port-au-Prince" }),
  new TimeZone({ name: "Havana", id: "America/Havana" }),
  new TimeZone({ name: "Indiana (East)", id: "America/Indianapolis" }),
  new TimeZone({ name: "Asuncion", id: "America/Asuncion" }),
  new TimeZone({ name: "Atlantic Time (Canada)", id: "America/Halifax" }),
  new TimeZone({ name: "Caracas", id: "America/Caracas" }),
  new TimeZone({ name: "Cuiaba", id: "America/Cuiaba" }),
  new TimeZone({ name: "Georgetown, La Paz, Manaus, San Juan", id: "America/La_Paz" }),
  new TimeZone({ name: "Santiago", id: "America/Santiago" }),
  new TimeZone({ name: "Turks and Caicos", id: "America/Grand_Turk" }),
  new TimeZone({ name: "Newfoundland", id: "America/St_Johns" }),
  new TimeZone({ name: "Araguaina", id: "America/Araguaina" }),
  new TimeZone({ name: "Brasilia", id: "America/Sao_Paulo" }),
  new TimeZone({ name: "Cayenne, Fortaleza", id: "America/Cayenne" }),
  new TimeZone({ name: "City of Buenos Aires", id: "America/Buenos_Aires" }),
  new TimeZone({ name: "Greenland", id: "America/Godthab" }),
  new TimeZone({ name: "Montevideo", id: "America/Montevideo" }),
  new TimeZone({ name: "Saint Pierre and Miquelon", id: "America/Miquelon" }),
  new TimeZone({ name: "Salvador", id: "America/Bahia" }),
  new TimeZone({ name: "Co-ordinated Universal Time-02", id: "Etc/GMT+2" }),
  new TimeZone({ name: "Azores", id: "Atlantic/Azores" }),
  new TimeZone({ name: "Cabo Verde Is.", id: "Atlantic/Cape_Verde" }),
  new TimeZone({ name: "Co-ordinated Universal Time", id: "UTC" }),
  new TimeZone({ name: "Casablanca", id: "Africa/Casablanca" }),
  new TimeZone({ name: "Dublin, Edinburgh, Lisbon, London", id: "Europe/London" }),
  new TimeZone({ name: "Monrovia, Reykjavik", id: "Atlantic/Reykjavik" }),
  new TimeZone({ name: "Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna", id: "Europe/Berlin" }),
  new TimeZone({ name: "Belgrade, Bratislava, Budapest, Ljubljana, Prague", id: "Europe/Budapest" }),
  new TimeZone({ name: "Brussels, Copenhagen, Madrid, Paris", id: "Europe/Paris" }),
  new TimeZone({ name: "Sarajevo, Skopje, Warsaw, Zagreb", id: "Europe/Warsaw" }),
  new TimeZone({ name: "West Central Africa", id: "Africa/Lagos" }),
  new TimeZone({ name: "Windhoek", id: "Africa/Windhoek" }),
  new TimeZone({ name: "Amman", id: "Asia/Amman" }),
  new TimeZone({ name: "Athens, Bucharest", id: "Europe/Bucharest" }),
  new TimeZone({ name: "Beirut", id: "Asia/Beirut" }),
  new TimeZone({ name: "Cairo", id: "Africa/Cairo" }),
  new TimeZone({ name: "Chisinau", id: "Europe/Chisinau" }),
  new TimeZone({ name: "Damascus", id: "Asia/Damascus" }),
  new TimeZone({ name: "Gaza, Hebron", id: "Asia/Gaza" }),
  new TimeZone({ name: "Harare, Pretoria", id: "Africa/Johannesburg" }),
  new TimeZone({ name: "Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius", id: "Europe/Kiev" }),
  new TimeZone({ name: "Istanbul", id: "Europe/Istanbul" }),
  new TimeZone({ name: "Jerusalem", id: "Asia/Jerusalem" }),
  new TimeZone({ name: "Kaliningrad", id: "Europe/Kaliningrad" }),
  new TimeZone({ name: "Tripoli", id: "Africa/Tripoli" }),
  new TimeZone({ name: "Baghdad", id: "Asia/Baghdad" }),
  new TimeZone({ name: "Kuwait, Riyadh", id: "Asia/Riyadh" }),
  new TimeZone({ name: "Minsk", id: "Europe/Minsk" }),
  new TimeZone({ name: "Moscow, St. Petersburg, Volgograd", id: "Europe/Moscow" }),
  new TimeZone({ name: "Nairobi", id: "Africa/Nairobi" }),
  new TimeZone({ name: "Tehran", id: "Asia/Tehran" }),
  new TimeZone({ name: "Abu Dhabi, Muscat", id: "Asia/Dubai" }),
  new TimeZone({ name: "Astrakhan, Ulyanovsk", id: "Europe/Astrakhan" }),
  new TimeZone({ name: "Baku", id: "Asia/Baku" }),
  new TimeZone({ name: "Izhevsk, Samara", id: "Europe/Samara" }),
  new TimeZone({ name: "Port Louis", id: "Indian/Mauritius" }),
  new TimeZone({ name: "Tbilisi", id: "Asia/Tbilisi" }),
  new TimeZone({ name: "Yerevan", id: "Asia/Yerevan" }),
  new TimeZone({ name: "Kabul", id: "Asia/Kabul" }),
  new TimeZone({ name: "Ashgabat, Tashkent", id: "Asia/Tashkent" }),
  new TimeZone({ name: "Ekaterinburg", id: "Asia/Yekaterinburg" }),
  new TimeZone({ name: "Islamabad, Karachi", id: "Asia/Karachi" }),
  new TimeZone({ name: "Chennai, Kolkata, Mumbai, New Delhi", id: "Asia/Calcutta" }),
  new TimeZone({ name: "Sri Jayawardenepura", id: "Asia/Colombo" }),
  new TimeZone({ name: "Kathmandu", id: "Asia/Kathmandu" }),
  new TimeZone({ name: "Astana", id: "Asia/Almaty" }),
  new TimeZone({ name: "Dhaka", id: "Asia/Dhaka" }),
  new TimeZone({ name: "Novosibirsk", id: "Asia/Novosibirsk" }),
  new TimeZone({ name: "Yangon (Rangoon)", id: "Asia/Rangoon" }),
  new TimeZone({ name: "Bangkok, Hanoi, Jakarta", id: "Asia/Bangkok" }),
  new TimeZone({ name: "Barnaul, Gorno-Altaysk", id: "Asia/Krasnoyarsk" }),
  new TimeZone({ name: "Hovd", id: "Asia/Hovd" }),
  new TimeZone({ name: "Tomsk", id: "Asia/Tomsk" }),
  new TimeZone({ name: "Beijing, Chongqing, Hong Kong SAR, Urumqi", id: "Asia/Shanghai" }),
  new TimeZone({ name: "Irkutsk", id: "Asia/Irkutsk" }),
  new TimeZone({ name: "Kuala Lumpur, Singapore", id: "Asia/Singapore" }),
  new TimeZone({ name: "Perth", id: "Australia/Perth" }),
  new TimeZone({ name: "Taipei", id: "Asia/Taipei" }),
  new TimeZone({ name: "Ulaanbaatar", id: "Asia/Ulaanbaatar" }),
  new TimeZone({ name: "Pyongyang", id: "Asia/Pyongyang" }),
  new TimeZone({ name: "Eucla", id: "Australia/Eucla" }),
  new TimeZone({ name: "Chita", id: "Asia/Chita" }),
  new TimeZone({ name: "Osaka, Sapporo, Tokyo", id: "Asia/Tokyo" }),
  new TimeZone({ name: "Seoul", id: "Asia/Seoul" }),
  new TimeZone({ name: "Yakutsk", id: "Asia/Yakutsk" }),
  new TimeZone({ name: "Adelaide", id: "Australia/Adelaide" }),
  new TimeZone({ name: "Darwin", id: "Australia/Darwin" }),
  new TimeZone({ name: "Brisbane", id: "Australia/Brisbane" }),
  new TimeZone({ name: "Canberra, Melbourne, Sydney", id: "Australia/Sydney" }),
  new TimeZone({ name: "Guam, Port Moresby", id: "Pacific/Port_Moresby" }),
  new TimeZone({ name: "Hobart", id: "Australia/Hobart" }),
  new TimeZone({ name: "Vladivostok", id: "Asia/Vladivostok" }),
  new TimeZone({ name: "Lord Howe Island", id: "Australia/Lord_Howe" }),
  new TimeZone({ name: "Bougainville Island", id: "Pacific/Bougainville" }),
  new TimeZone({ name: "Chokurdakh", id: "Asia/Srednekolymsk" }),
  new TimeZone({ name: "Magadan", id: "Asia/Magadan" }),
  new TimeZone({ name: "Norfolk Island", id: "Pacific/Norfolk" }),
  new TimeZone({ name: "Sakhalin", id: "Asia/Sakhalin" }),
  new TimeZone({ name: "Solomon Is., New Caledonia", id: "Pacific/Guadalcanal" }),
  new TimeZone({ name: "Anadyr, Petropavlovsk-Kamchatsky", id: "Asia/Kamchatka" }),
  new TimeZone({ name: "Auckland, Wellington", id: "Pacific/Auckland" }),
  new TimeZone({ name: "Co-ordinated Universal Time+12", id: "Etc/GMT-12" }),
  new TimeZone({ name: "Fiji", id: "Pacific/Fiji" }),
  new TimeZone({ name: "Chatham Islands", id: "Pacific/Chatham" }),
  new TimeZone({ name: "Nuku'alofa", id: "Pacific/Tongatapu" }),
  new TimeZone({ name: "Samoa", id: "Pacific/Apia" }),
  new TimeZone({ name: "Kiritimati Island", id: "Pacific/Kiritimati" })
];

function parseUtcOffset(s: string) {
  const [h, m] = s.substring(1).split(':').map(x => parseInt(x, 10));

  let value = h * 60 + m;

  if (s[0] === '-')
    value = -value;

  return value;
}

timeZones.sort((a, b) => a.utcOffset - b.utcOffset);

const timeZoneById = timeZones.reduce(
  (m, tz) => m.set(tz.id.toLowerCase(), tz),
  new Map<string, TimeZone>());

export function getTimeZoneById(id: string) {
  if (!id)
    return null;

  return timeZoneById.get(id.toLowerCase());
}

// TODO: Feed default from configuration
export const defaultTimeZone = getTimeZoneById('America/New_York') ?? getTimeZoneById('UTC');


/**
 * Converts UTC date time into a time zone specific date time.
 */
export function convertTimeToTimeZone(dt: Date | moment.Moment | string, tz: TimeZone | string) {
  const tzId = typeof tz === 'string' ? tz : tz.id;

  if (!moment.isMoment(dt))
    dt = moment(dt);

  return dt.tz(tzId).toISOString(true);
}

/**
 * Converts a date time in a specific time zone into a UTC date time.
 */
export function convertTimeToUtc(dt: Date | moment.Moment | string, tz: TimeZone | string) {
  const tzId = typeof tz === 'string' ? tz : tz.id;

  if (!moment.isMoment(dt))
    dt = moment(dt).tz(tzId, true);

  return dt.utc().toISOString(true);
}
